import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { createInvoice, getInvoices, sendInvoice, cancelInvoice } from '../../lib/api';
import { toast } from 'sonner';
import { FileText, Plus, Send, X, Loader2, DollarSign, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const MerchantInvoicesTab = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [form, setForm] = useState({
    customer_email: '',
    customer_name: '',
    items: [{ id: crypto.randomUUID(), description: '', quantity: 1, unit_price: '' }],
    currency: 'USD',
    notes: ''
  });

  useEffect(() => {
    loadInvoices();
  }, [filterStatus]);

  const loadInvoices = async () => {
    try {
      const status = filterStatus === 'all' ? null : filterStatus;
      const res = await getInvoices(status);
      setInvoices(res.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [...form.items, { id: crypto.randomUUID(), description: '', quantity: 1, unit_price: '' }]
    });
  };

  const removeItem = (index) => {
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index][field] = value;
    setForm({ ...form, items: newItems });
  };

  const calculateTotal = () => {
    return form.items.reduce((sum, item) => {
      return sum + (parseInt(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
    }, 0);
  };

  const handleCreate = async () => {
    if (!form.customer_email) {
      toast.error('Customer email is required');
      return;
    }
    if (form.items.some(i => !i.description || !i.unit_price)) {
      toast.error('All items must have description and price');
      return;
    }

    setCreating(true);
    try {
      await createInvoice({
        ...form,
        items: form.items.map(i => ({
          ...i,
          quantity: parseInt(i.quantity),
          unit_price: parseFloat(i.unit_price)
        })),
        send_email: true
      });
      toast.success('Invoice created and sent!');
      setShowCreate(false);
      setForm({
        customer_email: '',
        customer_name: '',
        items: [{ id: crypto.randomUUID(), description: '', quantity: 1, unit_price: '' }],
        currency: 'USD',
        notes: ''
      });
      loadInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const handleSend = async (id) => {
    try {
      await sendInvoice(id);
      toast.success('Invoice sent');
      loadInvoices();
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  const handleCancel = async (id) => {
    try {
      await cancelInvoice(id);
      toast.success('Invoice cancelled');
      loadInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel invoice');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-emerald-500/20 text-emerald-600 border-0"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>;
      case 'sent':
        return <Badge className="bg-blue-500/20 text-blue-600 border-0"><Send className="w-3 h-3 mr-1" />Sent</Badge>;
      case 'draft':
        return <Badge className="bg-slate-500/20 text-slate-600 border-0"><FileText className="w-3 h-3 mr-1" />Draft</Badge>;
      case 'overdue':
        return <Badge className="bg-red-500/20 text-red-600 border-0"><AlertCircle className="w-3 h-3 mr-1" />Overdue</Badge>;
      case 'cancelled':
        return <Badge className="bg-gray-500/20 text-gray-600 border-0"><X className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-6" data-testid="invoices-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-heading font-semibold">Invoices</h2>
        <div className="flex gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32 rounded-xl">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="rounded-xl" data-testid="create-invoice-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Create Invoice</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer Email *</Label>
                    <Input
                      type="email"
                      value={form.customer_email}
                      onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                      placeholder="customer@email.com"
                      className="rounded-xl"
                      data-testid="invoice-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Customer Name</Label>
                    <Input
                      value={form.customer_name}
                      onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                      placeholder="John Doe"
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Items</Label>
                  {form.items.map((item, idx) => (
                    <div key={item.id} className="flex gap-2 items-start">
                      <Input
                        value={item.description}
                        onChange={(e) => updateItem(idx, 'description', e.target.value)}
                        placeholder="Description"
                        className="flex-1 rounded-xl"
                      />
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                        className="w-16 rounded-xl"
                        min="1"
                      />
                      <Input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                        placeholder="Price"
                        className="w-24 rounded-xl"
                        step="0.01"
                      />
                      {form.items.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => removeItem(idx)} className="shrink-0">
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addItem} className="rounded-lg">
                    <Plus className="w-3 h-3 mr-1" /> Add Item
                  </Button>
                </div>

                <div className="flex justify-between items-center p-3 bg-muted rounded-xl">
                  <span className="font-medium">Total</span>
                  <span className="text-xl font-bold">${calculateTotal().toFixed(2)} {form.currency}</span>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCreate} disabled={creating} className="w-full rounded-xl" data-testid="submit-invoice">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create & Send Invoice'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {invoices.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No invoices yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {invoices.map((invoice, index) => (
            <motion.div
              key={invoice.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card data-testid={`invoice-${invoice.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-mono font-medium">{invoice.invoice_number}</p>
                        {getStatusBadge(invoice.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">{invoice.customer_email}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-lg">${invoice.total.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">
                          Due: {new Date(invoice.due_date).toLocaleDateString()}
                        </p>
                      </div>
                      {invoice.status === 'draft' && (
                        <Button variant="outline" size="sm" onClick={() => handleSend(invoice.id)} className="rounded-lg">
                          <Send className="w-4 h-4 mr-1" /> Send
                        </Button>
                      )}
                      {['draft', 'sent'].includes(invoice.status) && (
                        <Button variant="ghost" size="sm" onClick={() => handleCancel(invoice.id)} className="text-red-600">
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MerchantInvoicesTab;
